import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';

// Compact inline table-cell editor. Controlled by `value`; commits on blur / Enter
// only when changed, reverts on Esc. type='text' (watchlist overrides — may hold
// "20%" / "50 pts") or 'number' (position price levels).
export default function EditableCell({ value, onCommit, type = 'text', inputMode, placeholder = '—', width = '4.5rem' }) {
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);
  const revertingRef = useRef(false);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const commit = () => {
    // Skip the commit triggered by the blur() that follows an Esc revert —
    // at that point `draft` is still the stale (discarded) value.
    if (revertingRef.current) {
      revertingRef.current = false;
      return;
    }
    if (String(draft) !== String(value ?? '')) onCommit(draft);
  };

  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      // blur() fires onBlur → commit() once; don't commit twice (avoids a duplicate PATCH).
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      revertingRef.current = true;
      setDraft(value ?? '');
      inputRef.current?.blur();
    }
  };

  return (
    <input
      ref={inputRef}
      type={type}
      inputMode={inputMode}
      value={draft}
      placeholder={placeholder}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      style={{ width }}
      className={cn(
        'bg-bg-surface border border-bg-border rounded px-1.5 py-0.5',
        'text-xs font-mono text-txt-primary placeholder:text-txt-muted',
        'focus:outline-none focus:border-amber/60 focus:ring-1 focus:ring-amber/15',
        'transition-colors duration-150',
      )}
    />
  );
}
